import {
  COUPON_ISSUANCE_LABELS,
  COUPON_STATUS_LABELS,
  COUPON_STATUSES,
  type Coupon,
  type CouponCampaign,
  type CouponExport,
  type CouponStatus,
  POS_LINE_KIND_LABELS,
} from "@station/contracts";
import { Decimal, formatTrDate, Money } from "@station/core";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FileDown, Pencil, QrCode, TicketPlus, UserPlus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CancelCouponDialog,
  CouponCampaignDialog,
  CouponCustomerDialog,
  GenerateCodesDialog,
} from "../../../../features/loyalty/coupon-dialogs.js";
import {
  couponActions,
  couponCampaignActions,
  couponExportCsv,
  couponOwner,
  ownerText,
} from "../../../../features/loyalty/loyalty-values.js";
import {
  useCouponCampaign,
  useCouponCodes,
  useLoyaltyPermissions,
} from "../../../../features/loyalty/queries.js";
import {
  CampaignStatusBadge,
  CouponStatusBadge,
  LoyaltyPage,
  loyaltyCrumb,
  Stat,
  OwnerCell,
} from "../../../../features/loyalty/shared.js";
import { Section } from "../../../../features/fleet/shared.js";
import { validToDate } from "../../../../features/pos/pos-values.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel } from "../../../../lib/excel.js";
import { formatCount, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/sadakat/kuponlar/$id")({ component: CouponCampaignRoute });

/**
 * Kupon şablonu ayrıntısı (ADR 0017 §25): kurallar ve istatistik; kodlar (durum süzgeci, cursor sayfalama); toplu kod
 * üretimi (en çok 5.000, önek), dışa aktarım (Excel / CSV — sunucunun dışa aktarım ucundan, en çok 20.000 kod), elle
 * verilen şablonda müşteriye kod verme, kullanılmamış kodu müşteriye atama ve kod iptali.
 */
function CouponCampaignRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useLoyaltyPermissions();
  const campaign = useCouponCampaign(id);
  const data = campaign.data;
  const [dialog, setDialog] = useState<"edit" | "generate" | "issue" | null>(null);
  const actions = data ? couponCampaignActions(data) : null;

  return (
    <LoyaltyPage
      title={data ? `${data.code} · ${data.name}` : t("loyalty.coupons.detailTitle")}
      breadcrumb={[
        loyaltyCrumb(t("menu.loyaltyCoupons"), "/sadakat/kuponlar", data ? { company: data.companyId } : {}),
        { label: data?.code ?? "…" },
      ]}
      actions={
        data && actions ? (
          <>
            <ExportButtons campaign={data} />
            {permissions.canManageCoupons && actions.canGenerate ? (
              <Button onClick={() => setDialog("generate")}>
                <TicketPlus aria-hidden="true" />
                {t("loyalty.coupons.generate")}
              </Button>
            ) : null}
            {permissions.canManageCoupons && actions.canIssue ? (
              <Button onClick={() => setDialog("issue")}>
                <UserPlus aria-hidden="true" />
                {t("loyalty.coupons.issue")}
              </Button>
            ) : null}
            {permissions.canManageCoupons ? (
              <Button variant="outline" onClick={() => setDialog("edit")}>
                <Pencil aria-hidden="true" />
                {t("common.edit")}
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {campaign.error ? (
        <Alert tone="danger">{errorMessage(campaign.error)}</Alert>
      ) : !data || !actions ? (
        <SkeletonRows rows={8} />
      ) : (
        <>
          <CampaignSummary campaign={data} />
          <CodesSection campaign={data} />
          <CouponCampaignDialog
            open={dialog === "edit"}
            onOpenChange={(next) => setDialog(next ? "edit" : null)}
            companyId={data.companyId}
            campaign={data}
          />
          <GenerateCodesDialog
            open={dialog === "generate"}
            onOpenChange={(next) => setDialog(next ? "generate" : null)}
            campaign={data}
            remainingLimit={actions.remainingLimit}
          />
          <CouponCustomerDialog
            open={dialog === "issue"}
            onOpenChange={(next) => setDialog(next ? "issue" : null)}
            mode="issue"
            campaignId={data.id}
            coupon={null}
          />
        </>
      )}
    </LoyaltyPage>
  );
}

function conditionText(
  campaign: CouponCampaign,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const condition = campaign.earnCondition;
  if (!condition) return "—";
  if ("minBasketNet" in condition)
    return t("loyalty.coupons.conditionBasket", {
      amount: Money.fromMinor(condition.minBasketNet).formatTr(),
    });
  return t("loyalty.coupons.conditionLine", {
    quantity: Decimal.from(condition.minQuantity).formatTr(3),
    kind: POS_LINE_KIND_LABELS[condition.lineKind],
  });
}

function CampaignSummary({ campaign }: { campaign: CouponCampaign }): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("loyalty.coupons.summaryTitle")}>
      <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={t("common.status")}>
          <CampaignStatusBadge status={campaign.status} />
        </Stat>
        <Stat label={t("loyalty.coupons.campaign")} hint={campaign.campaignName}>
          <Link to="/kasa/kampanyalar" className="font-mono underline underline-offset-4">
            {campaign.campaignCode}
          </Link>
        </Stat>
        <Stat
          label={t("loyalty.coupons.fields.issuance")}
          hint={
            campaign.customerBound
              ? t("loyalty.coupons.customerBound")
              : t("loyalty.coupons.notCustomerBound")
          }
        >
          {COUPON_ISSUANCE_LABELS[campaign.issuance]}
        </Stat>
        <Stat label={t("loyalty.coupons.validity")}>
          <span className="tabular">
            {formatTrDate(new Date(campaign.validFrom))} –{" "}
            {campaign.validUntil
              ? formatTrDate(validToDate(campaign.validUntil))
              : t("loyalty.rules.openEnded")}
          </span>
        </Stat>
        <Stat label={t("loyalty.coupons.fields.codeValidityDays")}>
          {campaign.codeValidityDays === null
            ? "—"
            : t("loyalty.coupons.days", { days: formatCount(campaign.codeValidityDays) })}
        </Stat>
        <Stat label={t("loyalty.coupons.limits")}>
          <span className="grid text-sm font-normal">
            <span>{t("loyalty.coupons.perCode", { count: campaign.maxUsesPerCode })}</span>
            <span>
              {campaign.maxUsesPerCustomer === null
                ? t("loyalty.coupons.perCustomerNone")
                : t("loyalty.coupons.perCustomer", { count: campaign.maxUsesPerCustomer })}
            </span>
            <span>
              {campaign.totalLimit === null
                ? t("loyalty.coupons.totalNone")
                : t("loyalty.coupons.total", { count: formatCount(campaign.totalLimit) })}
            </span>
          </span>
        </Stat>
        {campaign.issuance === "EARNED" ? (
          <Stat label={t("loyalty.coupons.fields.earnCondition")}>{conditionText(campaign, t)}</Stat>
        ) : null}
        <Stat label={t("loyalty.coupons.stats")}>
          <span className="grid text-sm font-normal tabular" data-testid="coupon-stats">
            <span>{t("loyalty.coupons.statTotal", { count: formatCount(campaign.stats.total) })}</span>
            <span>
              {t("loyalty.coupons.statBreakdown", {
                issued: formatCount(campaign.stats.issued),
                redeemed: formatCount(campaign.stats.redeemed),
                expired: formatCount(campaign.stats.expired),
                cancelled: formatCount(campaign.stats.cancelled),
              })}
            </span>
          </span>
        </Stat>
      </dl>
    </Section>
  );
}

function CodesSection({ campaign }: { campaign: CouponCampaign }): ReactElement {
  const { t } = useTranslation();
  const permissions = useLoyaltyPermissions();
  const [status, setStatus] = useState<CouponStatus | "">("");
  const pages = useCursorPages(status);
  const codes = useCouponCodes(campaign.id, { status: status || undefined }, pages.cursor);
  const [target, setTarget] = useState<{ coupon: Coupon; mode: "assign" | "cancel" } | null>(null);

  const columns = useMemo<DataTableColumn<Coupon>[]>(
    () => [
      {
        id: "code",
        header: t("loyalty.coupons.fields.code"),
        hideable: false,
        exportValue: (row) => row.code,
        cell: (row) => <span className="font-mono whitespace-nowrap">{row.code}</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => COUPON_STATUS_LABELS[row.status],
        cell: (row) => <CouponStatusBadge status={row.status} />,
      },
      {
        id: "customer",
        header: t("loyalty.coupons.fields.customer"),
        exportValue: (row) => ownerText(couponOwner(row)),
        cell: (row) => <OwnerCell owner={couponOwner(row)} emptyText={t("loyalty.coupons.unassigned")} />,
      },
      {
        id: "uses",
        header: t("loyalty.coupons.uses"),
        align: "right",
        exportValue: (row) => row.uses,
        cell: (row) => (
          <span className="tabular">
            {row.uses} / {row.maxUses}
          </span>
        ),
      },
      {
        id: "validUntil",
        header: t("loyalty.coupons.validUntil"),
        exportValue: (row) => row.validUntil,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.validUntil)}</span>,
      },
      {
        id: "issuedAt",
        header: t("loyalty.coupons.issuedAt"),
        exportValue: (row) => row.issuedAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.issuedAt)}</span>,
      },
      {
        id: "actions",
        header: t("common.actions"),
        cell: (row) => {
          const actions = couponActions(row);
          if (!permissions.canManageCoupons || (!actions.canAssign && !actions.canCancel)) return null;
          return (
            <span className="flex flex-wrap gap-2">
              {actions.canAssign ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="relative"
                  onClick={() => setTarget({ coupon: row, mode: "assign" })}
                >
                  {t("loyalty.coupons.assign")}
                  <span className="sr-only"> — {row.code}</span>
                </Button>
              ) : null}
              {actions.canCancel ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative"
                  onClick={() => setTarget({ coupon: row, mode: "cancel" })}
                >
                  {t("loyalty.coupons.cancelCoupon")}
                  <span className="sr-only"> — {row.code}</span>
                </Button>
              ) : null}
            </span>
          );
        },
      },
    ],
    [t, permissions.canManageCoupons],
  );

  return (
    <Section title={t("loyalty.coupons.codesTitle")}>
      <DataTable
        caption={t("loyalty.coupons.codesCaption")}
        columns={columns}
        rows={codes.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={codes.isPending}
        error={codes.error ? <Alert tone="danger">{errorMessage(codes.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={3}
            icon={<QrCode />}
            title={t("loyalty.coupons.codesEmptyTitle")}
            description={t("loyalty.coupons.codesEmptyDescription")}
          />
        }
        filters={
          <FormField label={t("common.status")} className="w-48">
            {(control) => (
              <Select
                {...control}
                value={status}
                onChange={(event) => setStatus(event.target.value as CouponStatus | "")}
              >
                <option value="">{t("common.all")}</option>
                {COUPON_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {COUPON_STATUS_LABELS[value]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: codes.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(codes.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
      <CouponCustomerDialog
        open={target?.mode === "assign"}
        onOpenChange={(next) => {
          if (!next) setTarget(null);
        }}
        mode="assign"
        campaignId={null}
        coupon={target?.coupon ?? null}
      />
      <CancelCouponDialog
        open={target?.mode === "cancel"}
        onOpenChange={(next) => {
          if (!next) setTarget(null);
        }}
        coupon={target?.coupon ?? null}
      />
    </Section>
  );
}

type ExportRow = CouponExport["items"][number];

/** Dışa aktarım: sunucunun dışa aktarım ucundan (en çok 20.000 kod) Excel ya da `;` ayraçlı CSV. */
function ExportButtons({ campaign }: { campaign: CouponCampaign }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState<"xlsx" | "csv" | null>(null);

  async function run(kind: "xlsx" | "csv"): Promise<void> {
    setBusy(kind);
    try {
      const data = (
        await api.GET("/loyalty/coupon-campaigns/{id}/codes/export", {
          params: { path: { id: campaign.id } },
        })
      ).data!;
      const fileName = `kuponlar-${campaign.code}`;
      if (kind === "xlsx") {
        const columns: DataTableColumn<ExportRow>[] = [
          {
            id: "code",
            header: t("loyalty.coupons.fields.code"),
            cell: () => null,
            exportValue: (row) => row.code,
          },
          {
            id: "status",
            header: t("common.status"),
            cell: () => null,
            exportValue: (row) => COUPON_STATUS_LABELS[row.status],
          },
          {
            id: "customerCode",
            header: t("loyalty.coupons.export.customerCode"),
            cell: () => null,
            exportValue: (row) => row.customerCode,
          },
          {
            // Dışa aktarımda açık ad YOK: sunucunun maskeli adı.
            id: "customerName",
            header: t("loyalty.coupons.export.customerName"),
            cell: () => null,
            exportValue: (row) => row.customerMaskedName,
          },
          { id: "uses", header: t("loyalty.coupons.uses"), cell: () => null, exportValue: (row) => row.uses },
          {
            id: "validFrom",
            header: t("loyalty.coupons.fields.validFrom"),
            cell: () => null,
            exportValue: (row) => formatDateTime(row.validFrom),
          },
          {
            id: "validUntil",
            header: t("loyalty.coupons.validUntil"),
            cell: () => null,
            exportValue: (row) => (row.validUntil ? formatDateTime(row.validUntil) : null),
          },
          {
            id: "issuedAt",
            header: t("loyalty.coupons.issuedAt"),
            cell: () => null,
            exportValue: (row) => formatDateTime(row.issuedAt),
          },
        ];
        await exportRowsToExcel({ fileName, sheetName: campaign.code, columns, rows: data.items });
      } else {
        const csv = couponExportCsv(data, {
          headers: [
            t("loyalty.coupons.fields.code"),
            t("common.status"),
            t("loyalty.coupons.export.customerCode"),
            t("loyalty.coupons.export.customerName"),
            t("loyalty.coupons.uses"),
            t("loyalty.coupons.fields.validFrom"),
            t("loyalty.coupons.validUntil"),
            t("loyalty.coupons.issuedAt"),
          ],
          status: COUPON_STATUS_LABELS,
        });
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const anchor = Object.assign(document.createElement("a"), { href: url, download: `${fileName}.csv` });
        anchor.click();
        URL.revokeObjectURL(url);
      }
      toast({
        tone: "success",
        title: t("loyalty.coupons.exported", { count: formatCount(data.items.length) }),
      });
    } catch (error) {
      toast({ tone: "danger", title: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Button
        variant="outline"
        loading={busy === "xlsx"}
        disabled={busy !== null}
        onClick={() => void run("xlsx")}
      >
        <FileDown aria-hidden="true" />
        {t("loyalty.coupons.exportExcel")}
      </Button>
      <Button
        variant="outline"
        loading={busy === "csv"}
        disabled={busy !== null}
        onClick={() => void run("csv")}
      >
        <FileDown aria-hidden="true" />
        {t("loyalty.coupons.exportCsv")}
      </Button>
    </>
  );
}
